import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ArrowLeft, User, Users, Briefcase, Eye, Sparkles, ShieldAlert } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';

export default function AboutPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleBack = () => {
    if (localStorage.getItem('align_token')) {
      navigate('/dashboard');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="hero-section flex items-center justify-center p-4 sm:p-8 min-h-screen relative">
      <img
        src="https://images.unsplash.com/photo-1578258691902-327e3c08b7e9?crop=entropy&cs=srgb&fm=jpg&q=85"
        alt="Foggy Forest"
        className="hero-bg"
      />

      {/* Header Controls */}
      <div className="absolute top-4 left-4 z-20">
        <Button
          onClick={handleBack}
          variant="ghost"
          className="rounded-full bg-background/30 backdrop-blur-sm border border-border/40 hover:bg-background/50 text-foreground transition-all duration-300 flex items-center gap-2"
          data-testid="about-back-btn"
        >
          <ArrowLeft size={16} />
          <span>{t('about.back')}</span>
        </Button>
      </div>

      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>

      {/* Main Container */}
      <div className="relative z-10 max-w-4xl w-full mx-auto my-12" data-testid="about-page">
        <div className="glass-card rounded-2xl p-6 sm:p-10 shadow-2xl border border-border/50 backdrop-blur-md space-y-8 sm:space-y-12">
          
          {/* Hero Header */}
          <div className="text-center space-y-3">
            <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-foreground">
              {t('about.title')}
            </h1>
            <p className="font-serif text-lg sm:text-xl text-accent/90 italic">
              {t('about.tagline')}
            </p>
            <div className="w-24 h-0.5 bg-accent/40 mx-auto mt-4 rounded-full" />
          </div>

          {/* Philosophy Section */}
          <div className="space-y-4 text-center max-w-2xl mx-auto">
            <h2 className="font-serif text-2xl sm:text-3xl font-light text-foreground/90">
              {t('about.philosophy.title')}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              {t('about.philosophy.body')}
            </p>
          </div>

          {/* Core Framework Section */}
          <div className="space-y-6">
            <h2 className="font-serif text-2xl sm:text-3xl font-light text-center text-foreground/90">
              {t('about.framework.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* To Be */}
              <div className="bg-secondary/20 hover:bg-secondary/35 rounded-xl p-5 border border-border/40 transition-all duration-300 hover:-translate-y-1">
                <h3 className="font-serif text-lg font-medium text-foreground mb-2">
                  {t('about.framework.be.title')}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {t('about.framework.be.desc')}
                </p>
              </div>

              {/* To Want */}
              <div className="bg-secondary/20 hover:bg-secondary/35 rounded-xl p-5 border border-border/40 transition-all duration-300 hover:-translate-y-1">
                <h3 className="font-serif text-lg font-medium text-foreground mb-2">
                  {t('about.framework.want.title')}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {t('about.framework.want.desc')}
                </p>
              </div>

              {/* To Do */}
              <div className="bg-secondary/20 hover:bg-secondary/35 rounded-xl p-5 border border-border/40 transition-all duration-300 hover:-translate-y-1">
                <h3 className="font-serif text-lg font-medium text-foreground mb-2">
                  {t('about.framework.do.title')}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {t('about.framework.do.desc')}
                </p>
              </div>
            </div>
          </div>

          {/* Three Workspace Modes Section */}
          <div className="space-y-6">
            <h2 className="font-serif text-2xl sm:text-3xl font-light text-center text-foreground/90">
              {t('about.modes.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Personal Space */}
              <div className="bg-secondary/20 hover:bg-secondary/35 rounded-xl p-5 border border-border/40 flex flex-col items-center text-center transition-all duration-300">
                <div className="h-10 w-10 rounded-full bg-accent/15 flex items-center justify-center mb-3">
                  <User className="h-5 w-5 text-accent" />
                </div>
                <h3 className="font-serif text-base font-semibold text-foreground mb-2">
                  {t('about.modes.personal.title')}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {t('about.modes.personal.desc')}
                </p>
              </div>

              {/* Family Space */}
              <div className="bg-secondary/20 hover:bg-secondary/35 rounded-xl p-5 border border-border/40 flex flex-col items-center text-center transition-all duration-300">
                <div className="h-10 w-10 rounded-full bg-accent/15 flex items-center justify-center mb-3">
                  <Users className="h-5 w-5 text-accent" />
                </div>
                <h3 className="font-serif text-base font-semibold text-foreground mb-2">
                  {t('about.modes.family.title')}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {t('about.modes.family.desc')}
                </p>
              </div>

              {/* Corporate Space */}
              <div className="bg-secondary/20 hover:bg-secondary/35 rounded-xl p-5 border border-border/40 flex flex-col items-center text-center transition-all duration-300">
                <div className="h-10 w-10 rounded-full bg-accent/15 flex items-center justify-center mb-3">
                  <Briefcase className="h-5 w-5 text-accent" />
                </div>
                <h3 className="font-serif text-base font-semibold text-foreground mb-2">
                  {t('about.modes.corporate.title')}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {t('about.modes.corporate.desc')}
                </p>
              </div>
            </div>
          </div>

          {/* Design Values Section */}
          <div className="space-y-6">
            <h2 className="font-serif text-2xl sm:text-3xl font-light text-center text-foreground/90">
              {t('about.values.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Intentionality */}
              <div className="bg-secondary/10 rounded-xl p-5 border border-border/30 flex flex-col items-center text-center">
                <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center mb-2">
                  <Eye className="h-4 w-4 text-accent/80" />
                </div>
                <h4 className="font-serif text-sm font-medium text-foreground/80 mb-1">
                  {t('about.values.intentionality.title')}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {t('about.values.intentionality.desc')}
                </p>
              </div>

              {/* Reflection */}
              <div className="bg-secondary/10 rounded-xl p-5 border border-border/30 flex flex-col items-center text-center">
                <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center mb-2">
                  <Sparkles className="h-4 w-4 text-accent/80" />
                </div>
                <h4 className="font-serif text-sm font-medium text-foreground/80 mb-1">
                  {t('about.values.reflection.title')}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {t('about.values.reflection.desc')}
                </p>
              </div>

              {/* Calm Focus */}
              <div className="bg-secondary/10 rounded-xl p-5 border border-border/30 flex flex-col items-center text-center">
                <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center mb-2">
                  <ShieldAlert className="h-4 w-4 text-accent/80" />
                </div>
                <h4 className="font-serif text-sm font-medium text-foreground/80 mb-1">
                  {t('about.values.calm.title')}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {t('about.values.calm.desc')}
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
